/**
 * "Exclude" filters for security types, the three the bonds dashboard offers
 * (Convertible / Fixed to Floater / Variable Rate) plus Non-paying.
 *
 * The flags come from GET /api/tickers/types: { TICKER: 'c' | 'f' | 'v' | 'n' | combinations },
 * only for tickers that have one. A filter is a VIEW over the stored lists, never an
 * edit of them: the lists in the database keep every ticker, and switching a filter
 * off puts each ticker back exactly where the list had it (a trader's own additions
 * to a convertible-heavy list, and the sector lists alike).
 */

export const TYPE_FILTERS = [
  { key: 'convertible', flag: 'c', label: 'Convertible', hint: 'Bloomberg MTY_TYP names a conversion feature' },
  { key: 'fixedToFloater', flag: 'f', label: 'Fixed to Floater', hint: 'Coupon type VARIABLE: fixed now, floats after the reset' },
  { key: 'variableRate', flag: 'v', label: 'Variable Rate', hint: 'Coupon type FLOATING' },
  { key: 'nonPaying', flag: 'n', label: 'Non-paying', hint: 'No dividend paid for over 13 months (e.g. the Fannie Mae / Freddie Mac preferreds, last paid in 2008, whose yield is only an old dividend over a distressed price)' },
];

export const DEFAULT_TYPE_FILTERS = { convertible: false, fixedToFloater: false, variableRate: false, nonPaying: false };

const STORAGE_KEY = 'spreadDashboardTypeFilters';

export function loadTypeFilters() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (raw && typeof raw === 'object') {
      return Object.fromEntries(TYPE_FILTERS.map(f => [f.key, raw[f.key] === true]));
    }
  } catch (e) { /* private window or corrupt value: use the defaults */ }
  return { ...DEFAULT_TYPE_FILTERS };
}

export function saveTypeFilters(filters) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch (e) { /* not persisted */ }
}

/** The flag letters that are currently excluded, e.g. 'cf'. */
export function activeFlags(filters) {
  return TYPE_FILTERS.filter(f => filters && filters[f.key]).map(f => f.flag).join('');
}

/** Whether a ticker is excluded under the active filters. */
export function isExcluded(ticker, types, flags) {
  const t = types && types[ticker];
  if (!t || !flags) return false;
  for (const f of flags) if (t.includes(f)) return true;
  return false;
}

/**
 * The lists as the dashboard should show them: each with its excluded tickers
 * left out, order kept. Returns the SAME object when no filter is active (or no
 * flags are known yet) so memoised consumers do not re-run, and shares the array
 * of any list the filter does not touch.
 */
export function applyTypeFilters(lists, types, filters) {
  const flags = activeFlags(filters);
  if (!flags || !types || !lists) return lists;
  const out = {};
  for (const [name, tickers] of Object.entries(lists)) {
    const kept = tickers.filter(t => !isExcluded(t, types, flags));
    out[name] = kept.length === tickers.length ? tickers : kept;
  }
  return out;
}

/** How many tickers of the given lists each filter would exclude: { convertible: n, ... } */
export function countByType(lists, types) {
  const seen = new Set();
  Object.values(lists || {}).forEach(ts => ts.forEach(t => seen.add(t)));
  const counts = Object.fromEntries(TYPE_FILTERS.map(f => [f.key, 0]));
  if (!types) return counts;
  seen.forEach((t) => {
    const flags = types[t];
    if (!flags) return;
    TYPE_FILTERS.forEach(f => { if (flags.includes(f.flag)) counts[f.key] += 1; });
  });
  return counts;
}
